export class GeospatialQueryBuilder {
  constructor(locationData, availabilityFilters = []) {
    this.lat = locationData.lat;
    this.lng = locationData.lng;
    this.radius = locationData.radius;
    this.availabilityFilters = availabilityFilters;
  }

  buildGeoFilter() {
    const userLocationPoint = {
      type: 'point',
      coordinates: [this.lng, this.lat]
    };


    // Build filters based on selected availability types
    const shouldClauses = [];
    
    // If no availability filters are selected, include all types (default behavior)
    const hasAvailabilityFilter = this.availabilityFilters && this.availabilityFilters.length > 0;
    
    if (!hasAvailabilityFilter) {
      // No specific availability filter - include all matching locations
      shouldClauses.push(
        // Match physical addresses within radius
        {
          geo_distance: {
            distance: `${this.radius}km`,
            'addresses.coordinates': {
              lat: this.lat,
              lon: this.lng
            }
          }
        },
        // Match service areas (we_come_to_you) that intersect with user location
        {
          bool: {
            must: [
              { term: { 'addresses.type': 'we_come_to_you' } },
              {
                geo_shape: {
                  'addresses.area': {
                    shape: userLocationPoint,
                    relation: 'intersects'
                  }
                }
              }
            ]
          }
        },
        // Match delivery areas that intersect with user location  
        {
          bool: {
            must: [
              { term: { 'addresses.type': 'delivery' } },
              {
                geo_shape: {
                  'addresses.area': {
                    shape: userLocationPoint,
                    relation: 'intersects'
                  }
                }
              }
            ]
          }
        },
        // Match digital services (available everywhere)
        {
          term: {
            'addresses.type': 'digital'
          }
        }
      );
    } else {
      // Apply filters based on selected availability types
      if (this.availabilityFilters.includes('physical')) {
        // Only match physical addresses within radius
        shouldClauses.push({
          bool: {
            must: [
              { term: { 'addresses.type': 'physical' } },
              {
                geo_distance: {
                  distance: `${this.radius}km`,
                  'addresses.coordinates': {
                    lat: this.lat,
                    lon: this.lng
                  }
                }
              }
            ]
          }
        });
        
      }
      
      if (this.availabilityFilters.includes('we_come_to_you')) {
        // Only match service areas that intersect with user location
        shouldClauses.push({
          bool: {
            must: [
              { term: { 'addresses.type': 'we_come_to_you' } },
              {
                geo_shape: {
                  'addresses.area': {
                    shape: userLocationPoint,
                    relation: 'intersects'
                  }
                }
              }
            ]
          }
        });
      }
      
      if (this.availabilityFilters.includes('delivery')) {
        // Only match delivery areas that intersect with user location
        shouldClauses.push({
          bool: {
            must: [
              { term: { 'addresses.type': 'delivery' } },
              {
                geo_shape: {
                  'addresses.area': {
                    shape: userLocationPoint,
                    relation: 'intersects'
                  }
                }
              }
            ]
          }
        });
      }
      
      if (this.availabilityFilters.includes('digital')) {
        // Match digital services (available everywhere)
        shouldClauses.push({
          term: {
            'addresses.type': 'digital'
          }
        });
      }
    }

    const geoFilter = {
      nested: {
        path: 'addresses',
        query: {
          bool: {
            should: shouldClauses
          }
        }
      }
    };


    return geoFilter;
  }

  buildGeoSort() {
    return {
      "_geo_distance": {
        "addresses.coordinates": {
          lat: this.lat,
          lon: this.lng
        },
        order: "asc",
        unit: "km",
        mode: "min",
        nested: {
          path: "addresses"
        }
      }
    };
  }

  /**
   * Builds an Elasticsearch script to calculate and return the minimum distance
   * from the user's location to the nearest physical address of an offer.
   * 
   * Uses Haversine formula for distance calculation, matching the frontend
   * GeoCalculations utility for complete consistency. For nested geo_point fields,
   * accessing coordinates from _source is more reliable than doc values.
   * 
   * The script handles different types of addresses:
   * - Physical addresses: Calculate actual distance if within search radius
   * - Service areas (we_come_to_you): Available but no distance shown
   * - Delivery areas: Available but no distance shown  
   * - Digital services: Available everywhere, no distance shown
   * 
   * @returns {Object} Elasticsearch script configuration with distance calculation logic
   */
  buildDistanceScript() {
    return {
      script: {
        source: `
          // Initialize tracking variables
          double minDist = Double.MAX_VALUE;      // Tracks minimum distance to physical addresses
          boolean hasPolygonMatch = false;        // Indicates service/delivery area availability
          boolean hasPhysicalAddress = false;     // Indicates presence of physical locations
          boolean isDigitalService = false;       // Indicates digital/online availability
          
          // Process each address from source data
          // Note: For nested geo_point fields, we calculate distance from source coordinates
          // using the same Haversine formula as the frontend utility
          for (def address : params._source.addresses) {
            if (address.type == 'digital') {
              isDigitalService = true;
            }
            else if (address.coordinates != null && address.coordinates.lat != null && address.coordinates.lon != null) {
              hasPhysicalAddress = true;
              
              // Calculate distance using Haversine formula (matches frontend GeoCalculations utility)
              double lat1Rad = params.lat * Math.PI / 180.0;
              double lat2Rad = address.coordinates.lat * Math.PI / 180.0;
              double dLat = (address.coordinates.lat - params.lat) * Math.PI / 180.0;
              double dLon = (address.coordinates.lon - params.lon) * Math.PI / 180.0;
              
              double a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                        Math.cos(lat1Rad) * Math.cos(lat2Rad) *
                        Math.sin(dLon/2) * Math.sin(dLon/2);
              double c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
              double dist = 6371.0 * c; // Earth radius in km (matches GeoCalculations.EARTH_RADIUS_KM)
              
              // Only track distances within search radius (+1km buffer for edge cases)
              if (dist <= params.radius + 1 && dist < minDist) {
                minDist = dist;
              }
            }
            else if (address.area != null) {
              hasPolygonMatch = true;
            }
          }
          
          // Determine what to display based on address types found:
          
          // Digital services: available everywhere, no distance needed
          if (isDigitalService) {
            return null;
          }
          
          // Service/delivery areas without physical location: available but no specific distance
          if (hasPolygonMatch && !hasPhysicalAddress) {
            return null;
          }
          
          // Physical addresses exist but all are beyond search radius: not available
          if (hasPhysicalAddress && (minDist == Double.MAX_VALUE || minDist > params.radius + 1)) {
            return null;
          }
          
          // Physical address within radius found: return rounded distance (1 decimal place)
          if (minDist != Double.MAX_VALUE) {
            return Math.round(minDist * 10) / 10.0;
          }
          
          // Default: no distance information available
          return null;
        `,
        params: {
          lat: this.lat,
          lon: this.lng,
          radius: this.radius
        }
      }
    };
  }

  buildAvailabilityAggregation() {
    const userLocationPoint = {
      type: 'point',
      coordinates: [this.lng, this.lat]
    };


    const aggregation = {
      filter: {
        bool: {
          should: [
            // Count physical addresses within radius
            {
              bool: {
                must: [
                  { term: { 'addresses.type': 'physical' } },
                  {
                    geo_distance: {
                      distance: `${this.radius}km`,
                      'addresses.coordinates': {
                        lat: this.lat,
                        lon: this.lng
                      }
                    }
                  }
                ]
              }
            },
            // Count service areas that intersect with user location
            {
              bool: {
                must: [
                  { term: { 'addresses.type': 'we_come_to_you' } },
                  {
                    geo_shape: {
                      'addresses.area': {
                        shape: userLocationPoint,
                        relation: 'intersects'
                      }
                    }
                  }
                ]
              }
            },
            // Count delivery areas that intersect with user location
            {
              bool: {
                must: [
                  { term: { 'addresses.type': 'delivery' } },
                  {
                    geo_shape: {
                      'addresses.area': {
                        shape: userLocationPoint,
                        relation: 'intersects'
                      }
                    }
                  }
                ]
              }
            },
            // Count digital services (available everywhere)
            {
              term: {
                'addresses.type': 'digital'
              }
            }
          ]
        }
      },
      aggs: {
        availability: {
          terms: {
            field: 'addresses.type',
            size: 20
          },
          aggs: {
            offer_count: {
              reverse_nested: {}
            }
          }
        }
      }
    };

    return aggregation;
  }
}